import React, { useEffect, useState } from 'react';
import { Search, ChevronDown, ChevronRight, X, Trees, UtensilsCrossed, Dumbbell } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { POI, POICategory } from '../../types';
import { useTheme } from '../styles/ThemeContext';
interface POIFiltersProps {
  pois: POI[];
  selectedCategories: Set<POICategory>;
  onCategoryToggle: (category: POICategory) => void;
  onPOISelect: (poi: POI) => void;
}
// Map category to icon component
const getCategoryIcon = (category: POICategory) => {
  switch (category) {
    case 'Nature':
      return Trees;
    case 'Dining':
      return UtensilsCrossed;
    case 'Fitness':
      return Dumbbell;
    default:
      return Trees;
  }
};
// Map category to color classes
const getCategoryColor = (category: POICategory, isDark: boolean) => {
  switch (category) {
    case 'Nature':
      return isDark ? 'text-green-400 bg-green-500/20 border-green-500/35' : 'text-green-700 bg-green-500/15 border-green-400/30';
    case 'Dining':
      return isDark ? 'text-orange-400 bg-orange-500/20 border-orange-500/35' : 'text-orange-700 bg-orange-500/15 border-orange-400/30';
    case 'Fitness':
      return isDark ? 'text-blue-400 bg-blue-500/20 border-blue-500/35' : 'text-blue-700 bg-blue-500/15 border-blue-400/30';
    default:
      return isDark ? 'text-green-400 bg-green-500/20 border-green-500/35' : 'text-green-700 bg-green-500/15 border-green-400/30';
  }
};
export function POIFilters({
  pois,
  selectedCategories,
  onCategoryToggle,
  onPOISelect
}: POIFiltersProps) {
  const {
    isDark
  } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategory, setExpandedCategory] = useState<POICategory | null>('Nature');
  const categories: {
    id: POICategory;
    label: string;
  }[] = [{
    id: 'Nature',
    label: 'Nature'
  }, {
    id: 'Dining',
    label: 'Dining'
  }, {
    id: 'Fitness',
    label: 'Fitness'
  }];
  const filteredPOIs = pois.filter(poi => {
    const matchesSearch = poi.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategories.has(poi.category);
    return matchesSearch && matchesCategory;
  });
  const getPOIsByCategory = (categoryId: POICategory) => {
    return filteredPOIs.filter(p => p.category === categoryId);
  };
  // Auto-expand categories with search results
  useEffect(() => {
    if (searchQuery.trim()) {
      const categoryWithResults = categories.find(cat => getPOIsByCategory(cat.id).length > 0);
      if (categoryWithResults) {
        setExpandedCategory(categoryWithResults.id);
      }
    }
  }, [searchQuery, filteredPOIs]);
  const toggleCategory = (categoryId: POICategory) => {
    if (expandedCategory === categoryId) {
      setExpandedCategory(null);
    } else {
      setExpandedCategory(categoryId);
    }
  };
  return <div className={`fixed left-[100px] top-0 bottom-0 w-[320px] backdrop-blur-xl z-40 flex flex-col shadow-2xl transition-all duration-300 ${isDark ? 'bg-slate-900/70 border-r border-white/10' : 'bg-white/70 border-r border-white/40 shadow-xl'}`} data-id="element-188">
      {/* Header */}
      <div className={`p-6 transition-colors duration-300 ${isDark ? 'border-b border-white/10' : 'border-b border-white/40'}`} data-id="element-189">
        <div className="flex items-center justify-between mb-4" data-id="element-190">
          <h2 className={`text-lg font-light ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-191">
            Filter{' '}
            <span className={`font-bold ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-192">
              Nearby
            </span>
          </h2>
        </div>

        {/* Search */}
        <div className="relative" data-id="element-193">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} data-id="element-194" />
          <input type="text" placeholder="Search locations..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className={`w-full pl-10 pr-10 py-2 border rounded-lg text-sm focus:outline-none transition-colors ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-slate-500 focus:border-cyan-500/50' : 'bg-white/50 border-white/40 text-gray-900 placeholder-gray-400 focus:border-teal-500/50'}`} data-id="element-195" />
          {searchQuery && <button onClick={() => setSearchQuery('')} className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${isDark ? 'text-slate-400 hover:text-white' : 'text-gray-400 hover:text-gray-900'}`} data-id="element-196">
              <X className="w-4 h-4" data-id="element-197" />
            </button>}
        </div>
      </div>

      {/* Categories & Items List */}
      <div className="flex-1 overflow-y-auto custom-scrollbar" data-id="element-198">
        <div className="p-4 space-y-2" data-id="element-199">
          {categories.map(category => {
          const isSelected = selectedCategories.has(category.id);
          const isExpanded = expandedCategory === category.id;
          const categoryPOIs = getPOIsByCategory(category.id);
          const count = categoryPOIs.length;
          const Icon = getCategoryIcon(category.id);
          const colorClass = getCategoryColor(category.id, isDark);
          return <div key={category.id} className="space-y-1" data-id="element-200">
                {/* Category Header */}
                <div className="flex items-center gap-2" data-id="element-201">
                  <button onClick={() => toggleCategory(category.id)} className={`flex-1 flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors text-left ${isDark ? 'bg-slate-800/30 hover:bg-slate-800/50 border-white/5' : 'bg-white/40 hover:bg-white/60 border-white/40'}`} data-id="element-202">
                    <div className={`p-1.5 rounded-lg border ${colorClass}`} data-id="element-203">
                      <Icon className="w-4 h-4" data-id="element-204" />
                    </div>
                    <div className="flex-1" data-id="element-205">
                      <div className="flex items-center gap-2" data-id="element-206">
                        <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-207">
                          {category.label}
                        </span>
                        <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`} data-id="element-208">
                          ({count})
                        </span>
                      </div>
                    </div>
                    {isExpanded ? <ChevronDown className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} data-id="element-209" /> : <ChevronRight className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} data-id="element-210" />}
                  </button>

                  {/* Toggle Visibility */}
                  <button onClick={() => onCategoryToggle(category.id)} className={`p-2 rounded-lg border transition-all ${isSelected ? isDark ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-400' : 'bg-teal-500/15 border-teal-400/30 text-teal-600' : isDark ? 'bg-slate-800/30 border-white/5 text-slate-400 hover:text-white' : 'bg-white/40 border-white/40 text-gray-400 hover:text-gray-900'}`} aria-label={`Toggle ${category.label} visibility`} data-id="element-211">
                    {isSelected ? <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-212">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" data-id="element-213" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" data-id="element-214" />
                      </svg> : <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-215">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" data-id="element-216" />
                      </svg>}
                  </button>
                </div>

                {/* Expanded Items List */}
                <AnimatePresence data-id="element-217">
                  {isExpanded && isSelected && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.2
              }} className="overflow-hidden" data-id="element-218">
                      <div className="pl-4 space-y-1 py-1" data-id="element-219">
                        {categoryPOIs.map(poi => <button key={poi.id} onClick={() => onPOISelect(poi)} className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors text-left group ${isDark ? 'hover:bg-slate-800/50' : 'hover:bg-white/60'}`} data-id="element-220">
                            <span className={`text-sm transition-colors ${isDark ? 'text-slate-300 group-hover:text-white' : 'text-gray-600 group-hover:text-gray-900'}`} data-id="element-221">
                              {poi.name}
                            </span>
                          </button>)}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>
      </div>
    </div>;
}